import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Sparkles, Loader2, Plug, Unplug, Mic, User, Globe, Play,
  Eye, EyeOff, Film, X, RefreshCw, Trash2, ExternalLink, AlertTriangle, Download, FileText, Captions,
  Languages, Copy, Check, ChevronDown, ChevronUp,
} from "lucide-react";
import { toast } from "sonner";

import PageHeader from "@/components/PageHeader";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import ProviderRenderPanel from "@/components/avatar/ProviderRenderPanel";
import CompareMode from "@/components/avatar/CompareMode";
import AiWriteScriptModal from "@/components/AiWriteScriptModal";
import { appConfirm } from "@/components/AppDialogHost";

const PROVIDER_TABS = [
  { key: "synthesia", label: "Synthesia" },
  { key: "heygen",    label: "HeyGen" },
  { key: "did",       label: "D-ID" },
];


const STATUS_BADGE = {
  queued:       { cls: "bg-brand-amber/10 text-brand-amber border-brand-amber/30",   label: "Queued" },
  in_progress:  { cls: "bg-brand-blue/10 text-brand-blue border-brand-blue/30",     label: "Rendering" },
  complete:     { cls: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30", label: "Complete" },
  failed:       { cls: "bg-brand-onair/10 text-brand-onair border-brand-onair/30", label: "Failed" },
  cancelled:    { cls: "bg-ink-muted/10 text-ink-muted border-line",               label: "Cancelled" },
};

export default function AvatarStudio() {
  const { user } = useAuth();
  const isAdmin = ["super_admin", "sysadmin", "owner"].includes((user?.role || "").toLowerCase());
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [showConnect, setShowConnect] = useState(false);
  const [keyInput, setKeyInput] = useState("");
  const [keyVisible, setKeyVisible] = useState(false);
  const [connecting, setConnecting] = useState(false);

  const [avatars, setAvatars] = useState(null);
  const [voices, setVoices] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(false);

  const [templates, setTemplates] = useState([]);

  const [form, setForm] = useState({ title: "", script: "", avatar_id: "", voice_id: "", background: "off_white" });
  const [creating, setCreating] = useState(false);
  const [videos, setVideos] = useState({ items: [], total: 0 });
  const [refreshingId, setRefreshingId] = useState("");
  const [providersForCompare, setProvidersForCompare] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  const [aiWriteOpen, setAiWriteOpen] = useState(false);

  // Prefill from ?script= and ?title= query params (Send-to-Avatar buttons).
  useEffect(() => {
    const scr = searchParams.get("script");
    const tit = searchParams.get("title");
    if (!scr && !tit) return;
    try {
      const decoded = scr ? decodeURIComponent(escape(atob(scr))) : "";
      setForm((f) => ({
        ...f,
        script: decoded || f.script,
        title: tit || f.title,
      }));
      toast.success("Script imported — pick an avatar + voice to render");
      // Clear the query params so refresh doesn't re-prefill.
      const next = new URLSearchParams(searchParams);
      next.delete("script"); next.delete("title");
      setSearchParams(next, { replace: true });
    } catch {
      toast.error("Could not decode the imported script");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadStatus = useCallback(async () => {
    try {
      const r = await api.get("/avatar-studio/status");
      setStatus(r.data);
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.error("AI Avatar Studio is on Pro Radio + Network plans.");
      }
      setStatus({ connected: false });
    }
  }, []);

  const loadVideos = useCallback(async () => {
    try {
      const r = await api.get("/avatar-studio/videos");
      setVideos(r.data);
    } catch (e) { /* ignored */ }
  }, []);

  const loadCatalog = useCallback(async () => {
    setCatalogLoading(true);
    try {
      const [a, v, t] = await Promise.all([
        api.get("/avatar-studio/avatars"),
        api.get("/avatar-studio/voices"),
        api.get("/avatar-studio/templates"),
      ]);
      setAvatars(a.data.items || []);
      setVoices(v.data.items || []);
      setTemplates(t.data.items || []);
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(msg || "Couldn't load Synthesia catalog");
      setAvatars([]); setVoices([]);
    } finally { setCatalogLoading(false); }
  }, []);

  // ── Admin-only: refresh real Synthesia headshots for the curated stock avatars ──
  const [refreshingPreviews, setRefreshingPreviews] = useState(false);
  const [previewCacheBust, setPreviewCacheBust] = useState(0);

  const pollPreviewRefresh = useCallback(async () => {
    try {
      const r = await api.get("/avatar-studio/stock-avatars/refresh-status");
      const c = r.data.counts || {};
      const inflight = (c.queued || 0) + (c.creating || 0) + (c.polling || 0) + (c.downloading || 0);
      if (inflight > 0) {
        setTimeout(pollPreviewRefresh, 4000);
      } else {
        setRefreshingPreviews(false);
        setPreviewCacheBust(Date.now());
        const done = c.done || 0;
        const failed = c.failed || 0;
        if (failed === 0) toast.success(`Refreshed ${done} real Synthesia headshots`);
        else toast.warning(`Done: ${done} · Failed: ${failed} (Synthesia 500s — retry in a minute)`, { duration: 9000 });
      }
    } catch {
      setTimeout(pollPreviewRefresh, 6000);
    }
  }, []);

  const refreshRealPreviews = async () => {
    if (!isAdmin) return;
    if (!await appConfirm("Render free test videos against Synthesia to grab real headshots? Takes ~60 seconds.")) return;
    setRefreshingPreviews(true);
    try {
      const r = await api.post("/avatar-studio/stock-avatars/refresh-previews");
      toast.message(`Refreshing ${r.data.queued} avatar previews — polling…`);
      pollPreviewRefresh();
    } catch (e) {
      setRefreshingPreviews(false);
      toast.error(e?.response?.data?.detail || "Refresh failed");
    }
  };

  useEffect(() => { loadStatus(); loadVideos(); }, [loadStatus, loadVideos]);
  useEffect(() => {
    if (status?.connected && avatars === null) loadCatalog();
  }, [status, avatars, loadCatalog]);

  const connect = async (e) => {
    e?.preventDefault?.();
    if (!keyInput.trim()) return;
    setConnecting(true);
    try {
      const r = await api.post("/avatar-studio/connect", { api_key: keyInput.trim() });
      if (r.data.warning) {
        toast.warning(r.data.warning, { duration: 10000 });
      } else {
        toast.success("Synthesia connected");
      }
      setKeyInput(""); setShowConnect(false); setKeyVisible(false);
      loadStatus(); loadCatalog();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not connect — check the key");
    } finally { setConnecting(false); }
  };

  const disconnect = async () => {
    if (!await appConfirm("Disconnect Synthesia? Your videos stay; new renders will be blocked.")) return;
    try {
      await api.delete("/avatar-studio/connect");
      toast.success("Disconnected");
      setAvatars(null); setVoices(null);
      loadStatus();
    } catch (e) { toast.error(e?.response?.data?.detail || "Disconnect failed"); }
  };

  const create = async (e) => {
    e?.preventDefault?.();
    if (!form.script.trim() || !form.avatar_id) {
      toast.error("Script and avatar are required");
      return;
    }
    setCreating(true);
    try {
      await api.post("/avatar-studio/videos", {
        title: form.title || "Untitled",
        script: form.script,
        avatar_id: form.avatar_id,
        voice_id: form.voice_id,
        background: form.background || undefined,
        test: true,  // free test render — flip to false in Phase 2 with a confirmation modal
      });
      toast.success("Video queued · Synthesia will render in ~1–3 min");
      setForm({ ...form, script: "", title: "" });
      loadVideos(); loadStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed");
    } finally { setCreating(false); }
  };

  const refreshOne = async (video_id) => {
    setRefreshingId(video_id);
    try {
      const r = await api.get(`/avatar-studio/videos/${video_id}`);
      setVideos((prev) => ({
        ...prev,
        items: prev.items.map((v) => (v.video_id === video_id ? r.data : v)),
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed");
    } finally { setRefreshingId(""); }
  };

  const deleteOne = async (video_id) => {
    if (!await appConfirm("Remove this video record? The MP4 on Synthesia is not deleted.")) return;
    try {
      await api.delete(`/avatar-studio/videos/${video_id}`);
      loadVideos(); loadStatus();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  // ── Phase 3.5 — brand-kit overlay job tracking ──
  // brandJobs maps video_id → {job_id, status, branded}
  const [brandJobs, setBrandJobs] = useState({});

  const pollBrandJob = useCallback(async (video_id, job_id) => {
    try {
      const r = await api.get(`/avatar-studio/brand-jobs/${job_id}`);
      setBrandJobs((prev) => ({ ...prev, [video_id]: r.data }));
      if (r.data.status === "done") {
        toast.success("Brand kit applied — branded MP4 ready to download");
      } else if (r.data.status === "failed") {
        toast.error(`Brand overlay failed: ${r.data.error || "unknown error"}`, { duration: 10000 });
      } else {
        // still queued or processing — poll again in 4s
        setTimeout(() => pollBrandJob(video_id, job_id), 4000);
      }
    } catch {
      // transient — back off and retry once
      setTimeout(() => pollBrandJob(video_id, job_id), 6000);
    }
  }, []);

  const applyBrand = async (video_id) => {
    try {
      const r = await api.post(`/avatar-studio/videos/${video_id}/brand-overlay`);
      setBrandJobs((prev) => ({ ...prev, [video_id]: r.data }));
      toast.success("Brand overlay queued — we'll ping you when it's ready");
      pollBrandJob(video_id, r.data.job_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't queue brand overlay");
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto animate-fade-up" data-testid="avatar-studio">
      <Link to="/dashboard/vision-studio" className="inline-flex items-center gap-1 text-xs text-ink-muted hover:text-brand-amber transition mb-4">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to Vision Studio
      </Link>

      <PageHeader
        eyebrow="// AI AVATAR STUDIO"
        title="Turn any script into a presenter-led video."
        description="Pick a provider, pick an avatar, paste a script — get a broadcast-ready MP4. Modular framework: Synthesia (broadcast-grade), HeyGen (photo-real), and D-ID (lightweight, image-driven) all live here."
      />

      {/* Provider tab strip — Synthesia is the legacy/default; HeyGen & D-ID live in the multi-provider workspace */}
      <div className="mt-4 flex items-center gap-3 flex-wrap" data-testid="avatar-provider-tabs-row">
        <div className="inline-flex rounded-lg border border-line bg-bg-surface p-1" data-testid="avatar-provider-tabs">
          {PROVIDER_TABS.map((t) => {
            const active = (searchParams.get("provider") || "synthesia") === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => {
                  const sp = new URLSearchParams(searchParams);
                  if (t.key === "synthesia") sp.delete("provider"); else sp.set("provider", t.key);
                  setSearchParams(sp);
                }}
                data-testid={`avatar-provider-tab-${t.key}`}
                className={`px-4 py-1.5 text-xs font-semibold rounded-md transition ${active ? "bg-brand-amber text-bg" : "text-ink-secondary hover:text-ink-primary"}`}
              >
                {t.label}
              </button>
            );
          })}
        </div>
        <button
          type="button"
          onClick={async () => {
            try {
              const r = await api.get("/avatar-studio/providers");
              setProvidersForCompare(r.data.items || []);
              setShowCompare(true);
            } catch (e) {
              toast.error(e?.response?.data?.detail || "Could not load providers");
            }
          }}
          data-testid="avatar-compare-btn"
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-md border border-brand-amber/40 text-brand-amber hover:bg-brand-amber/10 transition"
        >
          <Sparkles className="h-3.5 w-3.5" /> Side-by-side compare
        </button>
      </div>

      <CompareMode
        open={showCompare}
        onClose={() => setShowCompare(false)}
        providers={providersForCompare}
        defaultScript={form.script}
        defaultTitle={form.title}
      />

      {/* HeyGen / D-ID — multi-provider workspace */}
      {(["heygen", "did"].includes(searchParams.get("provider") || "")) && (
        <div className="mt-6">
          <ProviderRenderPanel providerKey={searchParams.get("provider")} />
        </div>
      )}

      {/* Synthesia — full legacy workspace below (renders only when no `?provider` param or `?provider=synthesia`) */}
      {!(["heygen", "did"].includes(searchParams.get("provider") || "")) && <>

      {/* Connection card */}
      <div
        className="mt-6 rounded-xl border border-line bg-bg-surface p-5 flex items-center gap-4"
        data-testid="connection-card"
      >
        <div className={`h-12 w-12 rounded-md grid place-items-center shrink-0 ${status?.connected ? "bg-emerald-500/10 text-emerald-300" : "bg-brand-amber/10 text-brand-amber"}`}>
          {status?.connected ? <Plug className="h-5 w-5" /> : <Unplug className="h-5 w-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-base font-bold">Synthesia · BYOK</div>
          <div className="text-xs text-ink-muted">
            {status?.connected
              ? <>Connected · <strong>{status.video_count}</strong> video{status.video_count === 1 ? "" : "s"} rendered</>
              : "Bring your own Synthesia API key. Encrypted at rest — we never echo it back."}
          </div>
        </div>
        {status?.connected ? (
          <button
            onClick={disconnect}
            data-testid="disconnect-btn"
            className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md border border-brand-onair/30 text-brand-onair hover:bg-brand-onair/5"
          >
            <Unplug className="h-3.5 w-3.5" /> Disconnect
          </button>
        ) : (
          <button
            onClick={() => setShowConnect(true)}
            data-testid="connect-btn"
            className="inline-flex items-center gap-1 text-xs font-bold px-4 py-2 rounded-md bg-brand-amber text-bg hover:bg-brand-amber/90"
          >
            <Plug className="h-3.5 w-3.5" /> Connect API Key
          </button>
        )}
      </div>

      {!status?.connected ? (
        <>
          <div className="mt-6 rounded-xl border border-dashed border-line bg-bg-elevated p-8 text-center" data-testid="empty-state">
            <Film className="h-10 w-10 text-ink-muted mx-auto mb-3" />
            <div className="font-medium text-ink-primary mb-1">Connect Synthesia to start rendering avatar videos</div>
            <div className="text-xs text-ink-muted max-w-md mx-auto">
              Get a key from <a href="https://app.synthesia.io/" target="_blank" rel="noopener noreferrer" className="text-brand-amber hover:underline">app.synthesia.io</a> → <em>Settings → API access</em> (Creator plan and above). The key never leaves the backend — frontend only sees connection status.
            </div>
          </div>

          {/* Translation pipeline works even without Synthesia connected
              — it's pure managed-LLM. Lets users prep multilingual scripts
              in advance, then connect Synthesia when ready to render. */}
          <div className="mt-6 rounded-xl border border-line bg-bg-surface p-5 space-y-4">
            <div>
              <label className="text-[11px] font-mono uppercase tracking-wider text-ink-muted">Video title (optional)</label>
              <input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Top of the Hour News · Morning Promo"
                data-testid="form-title"
                className="w-full mt-1 rounded-md bg-bg-elevated border border-line px-3 py-2 text-sm focus:border-brand-amber/60 focus:outline-none"
              />
            </div>
            <div>
              <label className="text-[11px] font-mono uppercase tracking-wider text-ink-muted">Script</label>
              <textarea
                value={form.script}
                onChange={(e) => setForm({ ...form, script: e.target.value })}
                rows={6}
                placeholder="Paste or write a script in English. You can translate it now and render the video once you connect Synthesia."
                data-testid="form-script"
                className="w-full mt-1 rounded-md bg-bg-elevated border border-line px-3 py-2 text-sm focus:border-brand-amber/60 focus:outline-none font-mono"
              />
              <div className="text-[10px] text-ink-muted text-right">{form.script.length} / 8000 chars</div>
            </div>
            <TranslationPanel
              script={form.script}
              title={form.title}
              onUseTranslation={({ script, title }) => {
                setForm((f) => ({ ...f, script, title: title || f.title }));
                toast.success("Translation applied — connect Synthesia to render this video.");
              }}
            />
          </div>
        </>
      ) : (
        <>
          {/* Creator */}
          <div className="grid md:grid-cols-3 gap-4 mt-6">
            <form
              onSubmit={create}
              className="md:col-span-2 rounded-xl border border-line bg-bg-surface p-5 space-y-4"
              data-testid="create-form"
            >
              <div data-testid="template-picker">
                <label className="text-[11px] font-mono uppercase tracking-wider text-ink-muted">Start from a template</label>
                <select
                  onChange={(e) => {
                    const t = templates.find((x) => x.id === e.target.value);
                    if (!t) return;
                    setForm((f) => ({
                      ...f,
                      script: t.script,
                      title: t.name,
                      background: t.background || f.background,
                    }));
                    toast.success(`Loaded "${t.name}" — customise the [PLACEHOLDERS] and render`);
                    e.target.value = "";
                  }}
                  defaultValue=""
                  data-testid="template-select"
                  className="w-full mt-1 rounded-md bg-bg-elevated border border-line px-3 py-2 text-sm focus:border-brand-amber/60 focus:outline-none"
                >
                  <option value="">{templates.length === 0 ? "Loading 17 broadcaster templates…" : "Pick a broadcaster preset…"}</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.category} · {t.name} ({t.duration_sec}s)
                    </option>
                  ))}
                </select>
                <div className="text-[10px] text-ink-muted mt-1">17 presets: News, Music, Weather, Traffic, Promos, Commercials, Shows.</div>
              </div>
              <div>
                <label className="text-[11px] font-mono uppercase tracking-wider text-ink-muted">Video title</label>
                <input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. Top of the Hour News · Morning Promo"
                  data-testid="form-title"
                  className="w-full mt-1 rounded-md bg-bg-elevated border border-line px-3 py-2 text-sm focus:border-brand-amber/60 focus:outline-none"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-[11px] font-mono uppercase tracking-wider text-ink-muted">Script</label>
                  <button
                    type="button"
                    onClick={() => setAiWriteOpen(true)}
                    data-testid="ai-write-script-open"
                    className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider px-2.5 py-1 rounded-md border border-brand-amber/50 bg-brand-amber/10 text-brand-amber hover:bg-brand-amber/20 transition"
                    title="Let AI draft the script for you — pick a topic and length"
                  >
                    <Sparkles className="h-3 w-3" /> Write for me
                  </button>
                </div>
                <textarea
                  value={form.script}
                  onChange={(e) => setForm({ ...form, script: e.target.value })}
                  rows={6}
                  placeholder="Welcome to the BroadcastFlow newsroom. Today's top stories include…"
                  data-testid="form-script"
                  className="w-full rounded-md bg-bg-elevated border border-line px-3 py-2 text-sm focus:border-brand-amber/60 focus:outline-none font-mono"
                />
                <div className="text-[10px] text-ink-muted text-right">{form.script.length} / 8000 chars</div>
              </div>

              {/* Phase 4 — One-click multilingual translation pipeline */}
              <TranslationPanel
                script={form.script}
                title={form.title}
                connected={!!status?.connected}
                avatarId={form.avatar_id}
                voiceId={form.voice_id}
                background={form.background}
                onUseTranslation={({ script, title }) => {
                  setForm((f) => ({ ...f, script, title: title || f.title }));
                  toast.success("Translation applied to script box");
                }}
                onQueued={() => loadVideos && loadVideos()}
              />

              {/* Avatar — visual gallery (Synthesia killed the catalog endpoint;
                  we serve real Synthesia thumbnails or procedural fallback previews) */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-[11px] font-mono uppercase tracking-wider text-ink-muted">Avatar</label>
                  {isAdmin && (
                    <button
                      type="button"
                      onClick={refreshRealPreviews}
                      disabled={refreshingPreviews}
                      data-testid="refresh-previews-btn"
                      className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-md border border-brand-amber/40 bg-brand-amber/10 text-brand-amber hover:bg-brand-amber/20 disabled:opacity-50"
                      title="Admin only — renders one free test video per avatar to grab real Synthesia headshots (~60s)"
                    >
                      {refreshingPreviews ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                      {refreshingPreviews ? "Refreshing…" : "Refresh real previews"}
                    </button>
                  )}
                </div>
                {catalogLoading && !avatars ? (
                  <div className="text-xs text-ink-muted py-4">Loading avatars…</div>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2" data-testid="avatar-gallery">
                    {(avatars || []).map((a) => {
                      const isSelected = form.avatar_id === a.id;
                      const baseSrc = a.preview_url
                        ? (a.preview_url.startsWith("http") ? a.preview_url : `${process.env.REACT_APP_BACKEND_URL}${a.preview_url}`)
                        : null;
                      const src = baseSrc && previewCacheBust ? `${baseSrc}?v=${previewCacheBust}` : baseSrc;
                      return (
                        <button
                          key={a.id}
                          type="button"
                          onClick={() => setForm({ ...form, avatar_id: a.id })}
                          data-testid={`avatar-tile-${a.id}`}
                          className={`group relative rounded-md border p-1.5 transition text-left ${
                            isSelected
                              ? "border-brand-amber bg-brand-amber/10 amber-glow"
                              : "border-line bg-bg-elevated hover:border-brand-amber/40"
                          }`}
                          title={`${a.name}${a.gender ? ` · ${a.gender}` : ""}${a.language ? ` · ${a.language}` : ""}`}
                        >
                          {src ? (
                            <img src={src} alt={a.name}
                              className="w-full aspect-square rounded-md object-cover"
                              loading="lazy" />
                          ) : (
                            <div className="w-full aspect-square rounded-md bg-brand-amber/10 grid place-items-center text-2xl font-bold text-brand-amber">
                              {(a.name || "?")[0]}
                            </div>
                          )}
                          <div className="mt-1.5 text-[10px] font-medium leading-tight truncate">{a.name}</div>
                          <div className="text-[9px] font-mono uppercase tracking-wider text-ink-muted truncate">
                            {a.gender || "—"}{a.language ? ` · ${a.language}` : ""}
                          </div>
                          {isSelected && (
                            <div className="absolute top-1 right-1 h-4 w-4 rounded-full bg-brand-amber text-bg grid place-items-center text-[10px] font-bold">✓</div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
                {form.avatar_id && (
                  <div className="text-[10px] font-mono text-brand-amber mt-2" data-testid="avatar-selected">
                    Selected: {form.avatar_id}
                  </div>
                )}
              </div>
              <div>
                <label className="text-[11px] font-mono uppercase tracking-wider text-ink-muted">
                  Voice <span className="normal-case font-normal text-ink-muted/60">(auto-selected from avatar)</span>
                </label>
                <select
                  value={form.voice_id}
                  onChange={(e) => setForm({ ...form, voice_id: e.target.value })}
                  disabled={catalogLoading || !voices || (voices || []).length === 0}
                  data-testid="form-voice"
                  className="w-full mt-1 rounded-md bg-bg-elevated border border-line px-3 py-2 text-sm focus:border-brand-amber/60 focus:outline-none disabled:opacity-60"
                >
                  <option value="">{(voices || []).length === 0 ? "Auto (derived from avatar)" : `Choose from ${(voices || []).length} voices`}</option>
                  {(voices || []).map((v) => (
                    <option key={v.id} value={v.id}>{v.name}{v.language ? ` · ${v.language}` : ""}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[11px] font-mono uppercase tracking-wider text-ink-muted">Background</label>
                <select
                  value={form.background}
                  onChange={(e) => setForm({ ...form, background: e.target.value })}
                  data-testid="form-background"
                  className="w-full mt-1 rounded-md bg-bg-elevated border border-line px-3 py-2 text-sm focus:border-brand-amber/60 focus:outline-none"
                >
                  <option value="off_white">Off white</option>
                  <option value="green_screen">Green screen</option>
                  <option value="luxury_lobby">Luxury lobby</option>
                  <option value="modern_office">Modern office</option>
                  <option value="open_office">Open office</option>
                  <option value="warehouse">Warehouse</option>
                </select>
              </div>
              <div className="flex items-center justify-between pt-2">
                <div className="text-[11px] text-ink-muted inline-flex items-center gap-2">
                  <AlertTriangle className="h-3 w-3 text-brand-amber" />
                  Test mode is on by default — Synthesia adds a watermark but no credits are consumed.
                </div>
                <button
                  type="submit"
                  disabled={creating || catalogLoading}
                  data-testid="form-submit"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-brand-amber text-bg font-bold text-sm disabled:opacity-50 hover:bg-brand-amber/90 transition"
                >
                  {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  Render with Synthesia
                </button>
              </div>
            </form>

            {/* Catalog quick-stats */}
            <aside className="rounded-xl border border-line bg-bg-surface p-5 space-y-3" data-testid="catalog-summary">
              <div className="font-display text-sm font-bold">Catalog</div>
              {catalogLoading ? (
                <div className="text-xs text-ink-muted inline-flex items-center gap-1"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…</div>
              ) : (
                <>
                  <div className="flex items-center gap-2 text-xs">
                    <User className="h-3.5 w-3.5 text-brand-amber" />
                    <span className="text-ink-muted">Avatars</span>
                    <strong className="ml-auto">{(avatars || []).length}</strong>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <Mic className="h-3.5 w-3.5 text-brand-amber" />
                    <span className="text-ink-muted">Voices</span>
                    <strong className="ml-auto">{(voices || []).length}</strong>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    <Globe className="h-3.5 w-3.5 text-brand-amber" />
                    <span className="text-ink-muted">Languages</span>
                    <strong className="ml-auto">
                      {new Set((voices || []).map((v) => v.language).filter(Boolean)).size}
                    </strong>
                  </div>
                  <button
                    onClick={loadCatalog}
                    data-testid="catalog-refresh"
                    className="w-full text-[11px] mt-2 inline-flex items-center justify-center gap-1 py-1.5 rounded-md border border-line hover:border-brand-amber/40 transition"
                  >
                    <RefreshCw className="h-3 w-3" /> Refresh
                  </button>
                </>
              )}
            </aside>
          </div>

          {/* Video list */}
          <div className="mt-8" data-testid="video-list">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display text-lg font-bold">Recent renders ({videos.total})</div>
              <button
                onClick={loadVideos}
                data-testid="videos-refresh"
                className="text-[11px] text-ink-muted hover:text-brand-amber inline-flex items-center gap-1"
              >
                <RefreshCw className="h-3 w-3" /> Refresh
              </button>
            </div>
            {videos.items.length === 0 ? (
              <div className="rounded-md border border-dashed border-line bg-bg-elevated p-6 text-xs text-ink-muted text-center">
                No videos yet. Render one above to get started.
              </div>
            ) : (
              <div className="space-y-2">
                {videos.items.map((v) => {
                  const badge = STATUS_BADGE[v.status] || STATUS_BADGE.queued;
                  const isDone = v.status === "complete";
                  return (
                    <div
                      key={v.video_id}
                      className="flex items-center gap-3 rounded-md border border-line bg-bg-elevated p-3"
                      data-testid={`video-${v.video_id}`}
                    >
                      <div className="h-12 w-20 rounded bg-bg overflow-hidden shrink-0 grid place-items-center">
                        {v.thumbnail_url ? (
                          <img src={v.thumbnail_url} alt="" className="h-full w-full object-cover" />
                        ) : (
                          <Film className="h-5 w-5 text-ink-muted" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{v.title || v.video_id}</div>
                        <div className="text-[11px] text-ink-muted truncate">
                          {v.script_preview || "—"}
                        </div>
                      </div>
                      <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded border ${badge.cls}`}>{badge.label}</span>
                      {isDone && (
                        <>
                          <a
                            href={`${API_BASE}/avatar-studio/videos/${v.video_id}/download`}
                            target="_blank" rel="noopener noreferrer"
                            download={`${(v.title || "video").replace(/[^A-Za-z0-9._-]+/g, "-")}.mp4`}
                            data-testid={`download-${v.video_id}`}
                            className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border border-line hover:text-brand-amber hover:border-brand-amber/40 transition"
                            title="Download MP4"
                          >
                            <Download className="h-3.5 w-3.5" />
                          </a>
                          {/* Phase 3.5 — brand-kit overlay */}
                          {(() => {
                            const job = brandJobs[v.video_id];
                            const jobStatus = job?.status;
                            const busy = jobStatus === "queued" || jobStatus === "processing";
                            const ready = job?.branded || jobStatus === "done";
                            if (ready) {
                              return (
                                <a
                                  href={`${API_BASE}/avatar-studio/videos/${v.video_id}/branded-download`}
                                  target="_blank" rel="noopener noreferrer"
                                  download={`${(v.title || "video").replace(/[^A-Za-z0-9._-]+/g, "-")}-branded.mp4`}
                                  data-testid={`branded-download-${v.video_id}`}
                                  className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 transition"
                                  title="Download branded MP4"
                                >
                                  <Download className="h-3.5 w-3.5" /> Branded
                                </a>
                              );
                            }
                            return (
                              <button
                                onClick={() => applyBrand(v.video_id)}
                                disabled={busy}
                                data-testid={`brand-${v.video_id}`}
                                className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border border-brand-amber/40 bg-brand-amber/10 text-brand-amber hover:bg-brand-amber/20 disabled:opacity-50 transition"
                                title="Composite logo, lower-third, and station branding onto this video"
                              >
                                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                                {busy ? (jobStatus === "processing" ? "Rendering…" : "Queued…") : "Apply brand"}
                              </button>
                            );
                          })()}
                          <a
                            href={`${API_BASE}/avatar-studio/videos/${v.video_id}/captions.srt`}
                            target="_blank" rel="noopener noreferrer"
                            data-testid={`srt-${v.video_id}`}
                            className="inline-flex items-center gap-1 text-[10px] font-mono px-2 py-1 rounded-md border border-line hover:text-brand-blue hover:border-brand-blue/40 transition"
                            title="Download SRT subtitles"
                          >
                            <Captions className="h-3 w-3" /> SRT
                          </a>
                          <a
                            href={`${API_BASE}/avatar-studio/videos/${v.video_id}/captions.vtt`}
                            target="_blank" rel="noopener noreferrer"
                            data-testid={`vtt-${v.video_id}`}
                            className="inline-flex items-center gap-1 text-[10px] font-mono px-2 py-1 rounded-md border border-line hover:text-brand-blue hover:border-brand-blue/40 transition"
                            title="Download WebVTT subtitles"
                          >
                            <Captions className="h-3 w-3" /> VTT
                          </a>
                          <a
                            href={`${API_BASE}/avatar-studio/videos/${v.video_id}/captions.txt`}
                            target="_blank" rel="noopener noreferrer"
                            data-testid={`txt-${v.video_id}`}
                            className="inline-flex items-center gap-1 text-[10px] font-mono px-2 py-1 rounded-md border border-line hover:text-brand-blue hover:border-brand-blue/40 transition"
                            title="Download plain-text transcript"
                          >
                            <FileText className="h-3 w-3" /> TXT
                          </a>
                        </>
                      )}
                      {!["complete", "failed", "cancelled"].includes(v.status) && (
                        <button
                          onClick={() => refreshOne(v.video_id)}
                          disabled={refreshingId === v.video_id}
                          data-testid={`refresh-${v.video_id}`}
                          className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border border-line hover:text-brand-blue hover:border-brand-blue/40 transition"
                          title="Refresh status from Synthesia"
                        >
                          {refreshingId === v.video_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                        </button>
                      )}
                      <button
                        onClick={() => deleteOne(v.video_id)}
                        data-testid={`delete-${v.video_id}`}
                        className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md text-ink-muted hover:text-brand-onair hover:bg-brand-onair/10 transition"
                        title="Remove from list"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {/* Connect modal */}
      {showConnect && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-bg/80 backdrop-blur p-4" onClick={() => setShowConnect(false)} data-testid="connect-modal">
          <form
            onSubmit={connect}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-xl border border-line bg-bg-surface p-6 space-y-4"
          >
            <div className="flex items-start gap-3">
              <div className="rounded-md p-2 bg-brand-amber/10 text-brand-amber"><Plug className="h-5 w-5" /></div>
              <div className="flex-1 min-w-0">
                <h3 className="font-display text-lg font-bold">Connect Synthesia</h3>
                <p className="text-xs text-ink-muted mt-0.5">Paste your API key — we&rsquo;ll probe it against Synthesia and only persist it if it works.</p>
              </div>
              <button type="button" onClick={() => setShowConnect(false)} className="text-ink-muted hover:text-ink-primary" data-testid="connect-close">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div>
              <label className="text-[11px] font-mono uppercase tracking-wider text-ink-muted">Synthesia API key</label>
              <div className="mt-1 flex items-center gap-2">
                <input
                  type={keyVisible ? "text" : "password"}
                  value={keyInput}
                  onChange={(e) => setKeyInput(e.target.value)}
                  placeholder="Paste your key here…"
                  data-testid="connect-input"
                  autoComplete="off"
                  className="flex-1 rounded-md bg-bg-elevated border border-line px-3 py-2 text-sm font-mono focus:border-brand-amber/60 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={() => setKeyVisible((v) => !v)}
                  data-testid="connect-visibility"
                  className="h-9 w-9 grid place-items-center rounded-md border border-line text-ink-muted hover:text-brand-amber"
                  title={keyVisible ? "Hide" : "Reveal"}
                >
                  {keyVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <div className="mt-2 text-[10px] text-ink-muted">
                Get a key at <a href="https://app.synthesia.io/" target="_blank" rel="noopener noreferrer" className="text-brand-amber hover:underline">app.synthesia.io</a> → Settings → API access.
              </div>
            </div>
            <button
              type="submit"
              disabled={connecting || !keyInput.trim()}
              data-testid="connect-submit"
              className="w-full inline-flex items-center justify-center gap-2 py-2 rounded-md bg-brand-amber text-bg font-bold disabled:opacity-50 hover:bg-brand-amber/90 transition"
            >
              {connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />}
              {connecting ? "Probing…" : "Connect"}
            </button>
            <div className="text-[10px] text-ink-muted text-center">We probe Synthesia&rsquo;s catalog endpoint first — bad keys never touch storage.</div>
          </form>
        </div>
      )}
    </>}

    {/* AI script writer — helps clients get past the blank-page moment. */}
    <AiWriteScriptModal
      open={aiWriteOpen}
      onClose={() => setAiWriteOpen(false)}
      onApply={({ title, script }) => {
        setForm((f) => ({
          ...f,
          script,
          // Only overwrite title if it was empty — respect what the user typed.
          title: f.title?.trim() ? f.title : (title || f.title),
        }));
        setAiWriteOpen(false);
        toast.success("Script inserted. Tweak it, then hit Render.");
      }}
    />
    </div>
  );
}


// ──────────────────────────────────────────────────────────────
// Phase 4 — Translation panel.
// Collapsed by default. When the user wants a multilingual pass:
//   1. expand,
//   2. pick 1-8 target languages (chips, grouped by tier),
//   3. click "Translate" → backend fans out the LLM calls in parallel,
//   4. inline cards show each translation with Copy + "Use this script"
//      that loads it back into the form for an immediate render.
// ──────────────────────────────────────────────────────────────
function TranslationPanel({ script, title, onUseTranslation,
                           connected = false, avatarId = "", voiceId = "",
                           background = null, onQueued = null }) {
  const [expanded, setExpanded] = useState(false);
  const [catalog, setCatalog] = useState(null);
  const [picked, setPicked] = useState([]);   // array of language codes
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState(null); // last run payload
  const [copiedKey, setCopiedKey] = useState("");
  const [queueing, setQueueing] = useState(false);
  const [queueResult, setQueueResult] = useState(null);

  useEffect(() => {
    if (!expanded || catalog) return;
    (async () => {
      try {
        const r = await api.get("/avatar-studio/translation/languages");
        setCatalog(r.data);
      } catch { /* swallow — UI surfaces a soft error */ }
    })();
  }, [expanded, catalog]);

  const toggle = (code) => {
    setPicked((p) => p.includes(code) ? p.filter((c) => c !== code) : (p.length >= 8 ? p : [...p, code]));
  };

  const canRun = !!script.trim() && picked.length >= 1 && !running;

  const run = async () => {
    if (!canRun) return;
    setRunning(true);
    setResults(null);
    toast.message(`Translating into ${picked.length} ${picked.length === 1 ? "language" : "languages"} — Claude is writing…`, { duration: 6000 });
    try {
      const r = await api.post("/avatar-studio/translation/run", {
        script,
        title: title || undefined,
        target_languages: picked,
        source_language: "en",
      });
      setResults(r.data);
      const ok = r.data?.succeeded ?? 0;
      if (ok === picked.length) toast.success(`Translated into ${ok} ${ok === 1 ? "language" : "languages"} (${Math.round(r.data.duration_ms/100)/10}s).`);
      else toast.warning(`Translated ${ok} of ${picked.length}. See results below.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Translation failed");
    } finally { setRunning(false); }
  };

  const copy = async (key, txt) => {
    try {
      await navigator.clipboard.writeText(txt);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey((k) => k === key ? "" : k), 1400);
    } catch { toast.error("Copy failed"); }
  };

  return (
    <div className="rounded-md border border-line bg-bg-elevated" data-testid="translation-panel">
      <button
        type="button"
        onClick={() => setExpanded((x) => !x)}
        data-testid="translation-toggle"
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-bg/50 transition"
      >
        <div className="flex items-center gap-2">
          <Languages className="h-4 w-4 text-brand-amber" />
          <span className="text-xs font-mono uppercase tracking-wider text-brand-amber">// Translate</span>
          <span className="text-[11px] text-ink-muted">— one-click multilingual pass for the script + title{/* captions support via API */}</span>
        </div>
        {expanded ? <ChevronUp className="h-4 w-4 text-ink-muted" /> : <ChevronDown className="h-4 w-4 text-ink-muted" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-line">
          {!catalog ? (
            <div className="py-4 text-xs text-ink-muted">Loading language catalogue…</div>
          ) : (
            <>
              <TranslationLanguageRow title="Launch tier" items={catalog.launch} picked={picked} onToggle={toggle} testidPrefix="lang-launch" />
              <TranslationLanguageRow title="Phase 1 tier" items={catalog.phase1} picked={picked} onToggle={toggle} testidPrefix="lang-phase1" />
              <TranslationLanguageRow title="Caribbean tier (BroadcastFlow exclusive)" items={catalog.caribbean} picked={picked} onToggle={toggle} testidPrefix="lang-caribbean" />

              <div className="flex items-center justify-between mt-4">
                <div className="text-[11px] font-mono text-ink-muted">
                  {picked.length === 0 ? "Pick up to 8 target languages" :
                   picked.length >= 8 ? <span className="text-brand-amber">8 / 8 max — deselect one to add more</span> :
                   `${picked.length} of 8 selected`}
                </div>
                <button
                  type="button"
                  onClick={run}
                  disabled={!canRun}
                  data-testid="translation-run"
                  className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-md bg-brand-amber text-bg font-bold disabled:opacity-40 amber-glow"
                >
                  {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Languages className="h-4 w-4" />}
                  {running ? "Translating…" : "Translate"}
                </button>
              </div>

              {!script.trim() && (
                <div className="text-[11px] text-brand-onair mt-2">Paste or generate a script above first.</div>
              )}

              {results && (
                <>
                  <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="translation-results">
                    {results.translations.map((tr) => (
                      <TranslationResultCard
                        key={tr.language}
                        result={tr}
                        copiedKey={copiedKey}
                        onCopy={copy}
                        onUse={() => onUseTranslation({ script: tr.script, title: tr.title })}
                      />
                    ))}
                  </div>

                  {/* Phase 4.1 — Multilingual fan-out renderer.
                      Only renders when Synthesia is connected. The user
                      already picked one avatar + voice in the form above;
                      we reuse that for every render so they ship N localised
                      videos with a single click. */}
                  {connected && results.succeeded > 0 && (
                    <BulkQueueBar
                      connected={connected}
                      avatarId={avatarId}
                      voiceId={voiceId}
                      results={results}
                      baseTitle={title}
                      background={background}
                      queueing={queueing}
                      queueResult={queueResult}
                      onQueue={async (testMode) => {
                        if (!avatarId) {
                          toast.error("Pick an avatar in the form above first.");
                          return;
                        }
                        setQueueing(true);
                        setQueueResult(null);
                        const okItems = results.translations.filter((t) => t.ok);
                        toast.message(`Queueing ${okItems.length} Synthesia ${okItems.length === 1 ? "render" : "renders"}…`, { duration: 6000 });
                        try {
                          const r = await api.post("/avatar-studio/translation/queue-videos", {
                            translations: okItems.map((t) => ({
                              language: t.language,
                              language_name: t.language_name,
                              script: t.script,
                              title: t.title || undefined,
                            })),
                            avatar_id: avatarId,
                            voice_id: voiceId,
                            background: background || undefined,
                            base_title: title || "Multilingual broadcast",
                            test: testMode,
                            translation_id: results.translation_id,
                          });
                          setQueueResult(r.data);
                          if (r.data.succeeded === r.data.count) {
                            toast.success(`Queued ${r.data.succeeded} ${r.data.succeeded === 1 ? "video" : "videos"} on Synthesia.`);
                          } else {
                            toast.warning(`Queued ${r.data.succeeded} of ${r.data.count} — see the failed list below.`);
                          }
                          onQueued && onQueued(r.data);
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "Queue failed");
                        } finally {
                          setQueueing(false);
                        }
                      }}
                    />
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function TranslationLanguageRow({ title, items, picked, onToggle, testidPrefix }) {
  if (!items?.length) return null;
  const atCap = picked.length >= 8;
  return (
    <div className="mt-3">
      <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-1.5">{title}</div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((L) => {
          const on = picked.includes(L.code);
          const dimmed = atCap && !on;
          return (
            <button
              key={L.code}
              type="button"
              onClick={() => onToggle(L.code)}
              data-testid={`${testidPrefix}-${L.code}`}
              disabled={dimmed}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border transition ${
                on
                  ? "border-brand-amber bg-brand-amber/15 text-brand-amber"
                  : dimmed
                  ? "border-line bg-bg text-ink-muted opacity-40 cursor-not-allowed"
                  : "border-line bg-bg text-ink-secondary hover:border-brand-amber/30"
              }`}
              title={dimmed ? "8 language max — deselect one to add more" : L.native}
            >
              <span>{L.flag}</span>
              <span className="font-medium">{L.name}</span>
              {on && <Check className="h-3 w-3" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TranslationResultCard({ result, copiedKey, onCopy, onUse }) {
  const r = result;
  const okClass = r.ok ? "border-emerald-500/30" : "border-brand-onair/40";
  const flagFromCatalog = LANGUAGE_FLAG_BY_CODE[r.language] || "🌐";
  return (
    <div className={`rounded-md border ${okClass} bg-bg p-3`} data-testid={`translation-card-${r.language}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-base">{flagFromCatalog}</span>
          <div>
            <div className="text-sm font-bold leading-tight">{r.language_name}</div>
            <div className="text-[10px] font-mono text-ink-muted">{r.native_name}</div>
          </div>
        </div>
        {r.ok ? (
          <span className="text-[10px] font-mono px-2 py-0.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-300">OK</span>
        ) : (
          <span className="text-[10px] font-mono px-2 py-0.5 rounded-full border border-brand-onair/40 bg-brand-onair/10 text-brand-onair">FAIL</span>
        )}
      </div>

      {r.ok ? (
        <>
          {r.title && (
            <div className="mb-2">
              <div className="text-[9px] font-mono uppercase tracking-wider text-ink-muted">Title</div>
              <div className="text-xs">{r.title}</div>
            </div>
          )}
          <div>
            <div className="text-[9px] font-mono uppercase tracking-wider text-ink-muted">Script</div>
            <pre className="text-xs whitespace-pre-wrap font-mono leading-relaxed max-h-44 overflow-auto bg-bg-elevated p-2 rounded mt-1" data-testid={`translation-script-${r.language}`}>{r.script}</pre>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <button
              type="button"
              onClick={() => onCopy(`script_${r.language}`, r.script)}
              data-testid={`translation-copy-${r.language}`}
              className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-md border border-line hover:border-brand-amber/40"
            >
              {copiedKey === `script_${r.language}` ? <Check className="h-3 w-3 text-emerald-300" /> : <Copy className="h-3 w-3" />}
              {copiedKey === `script_${r.language}` ? "Copied" : "Copy script"}
            </button>
            <button
              type="button"
              onClick={onUse}
              data-testid={`translation-use-${r.language}`}
              className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-md bg-brand-amber/15 text-brand-amber border border-brand-amber/40 hover:bg-brand-amber/25"
            >
              Use this script
            </button>
          </div>
        </>
      ) : (
        <div className="text-xs text-brand-onair">{r.error || "Translation failed."}</div>
      )}
    </div>
  );
}

// Cached locally so we don't pay a per-card lookup hit; populated by
// TranslationPanel's catalog fetch via a side effect (small, static).
const LANGUAGE_FLAG_BY_CODE = {
  en: "🇺🇸", es: "🇪🇸", fr: "🇫🇷", pt: "🇵🇹",
  de: "🇩🇪", it: "🇮🇹", nl: "🇳🇱", ja: "🇯🇵", zh: "🇨🇳", ko: "🇰🇷",
  jam: "🇯🇲", ht: "🇭🇹", "es-do": "🇩🇴", tt: "🇹🇹",
};


// ──────────────────────────────────────────────────────────────
// Phase 4.1 — Multilingual fan-out queue bar.
// Sticks at the bottom of the translation results grid. Two buttons:
//   • Queue N test renders (free, watermarked — default)
//   • Queue N paid renders (consumes Synthesia credits — confirmation gate)
// Shows the per-language outcome inline after the API responds.
// ──────────────────────────────────────────────────────────────
function BulkQueueBar({ connected, avatarId, voiceId, results, baseTitle,
                       background, queueing, queueResult, onQueue }) {
  const okCount = results.translations.filter((t) => t.ok).length;
  const canQueue = connected && !!avatarId && okCount > 0 && !queueing;
  // ─── Bulk-publish state (Phase 4.2) ───────────────────────────
  const [publishStatus, setPublishStatus] = useState("draft");
  const [publishing, setPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState(null);
  const queuedIds = queueResult?.queued?.map((q) => q.video_id) || [];
  const canPublish = queuedIds.length > 0 && !publishing;

  const handlePublish = async () => {
    if (!canPublish) return;
    if (publishStatus === "publish") {
      const ok = await appConfirm(
        `Publish ${queuedIds.length} ${queuedIds.length === 1 ? "video" : "videos"} live on your WordPress site? Renders still in progress will be skipped — re-run after they finish.`,
      );
      if (!ok) return;
    }
    setPublishing(true);
    setPublishResult(null);
    toast.message(`Publishing to WordPress…`, { duration: 6000 });
    try {
      const r = await api.post("/avatar-studio/translation/queue-publish", {
        video_ids: queuedIds,
        status: publishStatus,
        require_ready: true,
      });
      setPublishResult(r.data);
      const pub = r.data.published?.length || 0;
      const skip = r.data.skipped?.length || 0;
      const fail = r.data.failed?.length || 0;
      if (pub > 0) toast.success(`Published ${pub} ${pub === 1 ? "post" : "posts"} to WordPress.${skip ? ` ${skip} still rendering.` : ""}${fail ? ` ${fail} failed.` : ""}`);
      else if (skip > 0) toast.warning(`All ${skip} videos still rendering — try again once they're complete.`);
      else toast.error("Publish failed — see details below.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk publish failed");
    } finally { setPublishing(false); }
  };
  return (
    <div className="mt-5 rounded-md border border-brand-amber/30 bg-brand-amber/5 p-4" data-testid="bulk-queue-bar">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="text-xs font-bold text-brand-amber inline-flex items-center gap-2">
            <Film className="h-3.5 w-3.5" />
            Render {okCount} {okCount === 1 ? "video" : "videos"} in one click
          </div>
          <div className="text-[11px] text-ink-muted mt-1 max-w-xl">
            Reuses the avatar + voice selected above. Each language becomes its own Synthesia job and shows up in the videos list as soon as it finishes.
          </div>
          {!avatarId && (
            <div className="text-[11px] text-brand-onair mt-1">Pick an avatar in the form above before queueing.</div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => onQueue(true)}
            disabled={!canQueue}
            data-testid="bulk-queue-test"
            className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border border-brand-amber/40 text-brand-amber hover:bg-brand-amber/15 disabled:opacity-40"
            title="Test renders are free but Synthesia-watermarked. Good for QA before flipping the toggle to paid."
          >
            {queueing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Film className="h-3 w-3" />}
            Queue {okCount} test {okCount === 1 ? "render" : "renders"}
          </button>
          <button
            type="button"
            onClick={async () => {
              const ok = await appConfirm(
                `This will consume ${okCount} Synthesia render ${okCount === 1 ? "credit" : "credits"} from your account. Proceed?`,
              );
              if (ok) onQueue(false);
            }}
            disabled={!canQueue}
            data-testid="bulk-queue-paid"
            className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md bg-brand-amber text-bg font-bold amber-glow disabled:opacity-40"
            title="Production-grade render — uses your paid Synthesia credits."
          >
            {queueing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Film className="h-3 w-3" />}
            Queue {okCount} paid {okCount === 1 ? "render" : "renders"}
          </button>
        </div>
      </div>

      {queueResult && (
        <div className="mt-4 space-y-1" data-testid="bulk-queue-results">
          {queueResult.queued.map((q) => (
            <div key={q.video_id} className="flex items-center gap-2 text-[11px]">
              <span className="text-base">{LANGUAGE_FLAG_BY_CODE[q.language] || "🌐"}</span>
              <span className="font-medium text-ink-secondary">{q.language_name}</span>
              <span className="text-ink-muted">·</span>
              <span className="font-mono text-emerald-300">{q.status}</span>
              <span className="text-ink-muted">·</span>
              <span className="font-mono text-ink-muted">{q.video_id.slice(0, 14)}…</span>
            </div>
          ))}
          {queueResult.failed.map((f, i) => (
            <div key={`fail_${i}`} className="flex items-center gap-2 text-[11px]">
              <span className="text-base">{LANGUAGE_FLAG_BY_CODE[f.language] || "🌐"}</span>
              <span className="font-medium text-ink-secondary">{f.language_name}</span>
              <span className="text-ink-muted">·</span>
              <span className="font-mono text-brand-onair">{f.error}</span>
            </div>
          ))}
        </div>
      )}

      {/* Phase 4.2 — Bulk publish to WordPress.
          Surfaces ONLY after the user has queued renders. Each render
          becomes one localised WP post with a {base-slug}-{language} URL. */}
      {queueResult && queuedIds.length > 0 && (
        <div className="mt-4 pt-4 border-t border-brand-amber/20" data-testid="bulk-publish-bar">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <div className="text-xs font-bold text-brand-amber inline-flex items-center gap-2">
                <ExternalLink className="h-3.5 w-3.5" />
                Publish {queuedIds.length} {queuedIds.length === 1 ? "video" : "videos"} to WordPress
              </div>
              <div className="text-[11px] text-ink-muted mt-1 max-w-xl">
                One localised post per language — slugs follow <code className="font-mono">{`{base-slug}-{lang}`}</code>. Renders still in progress are skipped automatically; re-run once they finish.
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={publishStatus}
                onChange={(e) => setPublishStatus(e.target.value)}
                data-testid="bulk-publish-status"
                className="rounded-md bg-bg border border-line px-2 py-1.5 text-xs focus:border-brand-amber/60 focus:outline-none"
              >
                <option value="draft">As drafts</option>
                <option value="publish">Publish live</option>
              </select>
              <button
                type="button"
                onClick={handlePublish}
                disabled={!canPublish}
                data-testid="bulk-publish-btn"
                className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md bg-brand-amber text-bg font-bold amber-glow disabled:opacity-40"
              >
                {publishing ? <Loader2 className="h-3 w-3 animate-spin" /> : <ExternalLink className="h-3 w-3" />}
                Publish all
              </button>
            </div>
          </div>

          {publishResult && (
            <div className="mt-3 space-y-1" data-testid="bulk-publish-results">
              {publishResult.published.map((p) => (
                <div key={p.video_id} className="flex items-center gap-2 text-[11px]">
                  <span className="text-base">{LANGUAGE_FLAG_BY_CODE[p.language] || "🌐"}</span>
                  <span className="font-medium text-ink-secondary">{p.language_name}</span>
                  <span className="text-ink-muted">·</span>
                  <span className="font-mono text-emerald-300">published</span>
                  {p.wp_link && (
                    <a
                      href={p.wp_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`bulk-publish-link-${p.language}`}
                      className="font-mono text-brand-amber hover:underline inline-flex items-center gap-1"
                    >
                      {p.slug} <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              ))}
              {publishResult.skipped.map((s) => (
                <div key={`skip_${s.video_id}`} className="flex items-center gap-2 text-[11px]">
                  <span className="text-base">{LANGUAGE_FLAG_BY_CODE[s.language] || "🌐"}</span>
                  <span className="font-medium text-ink-secondary">{s.language_name}</span>
                  <span className="text-ink-muted">·</span>
                  <span className="font-mono text-ink-muted">{s.reason}</span>
                </div>
              ))}
              {publishResult.failed.map((f, i) => (
                <div key={`pubfail_${i}`} className="flex items-center gap-2 text-[11px]">
                  <span className="text-base">{LANGUAGE_FLAG_BY_CODE[f.language] || "🌐"}</span>
                  <span className="font-medium text-ink-secondary">{f.language_name}</span>
                  <span className="text-ink-muted">·</span>
                  <span className="font-mono text-brand-onair">{f.error}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
